var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Injects the signature HTML into the current Outlook compose item.
 *
 * INJECTION STRATEGY (tiered fallback):
 *
 * Tier 1 — setSignatureAsync (Mailbox 1.10+, Outlook 2021+)
 *   - Purpose-built API for signatures
 *   - Inserts into the "signature zone" without touching body text
 *   - Works correctly for new compose, reply, and forward
 *   - Respects Outlook's "don't auto-insert" setting if the user disabled it
 *
 * Tier 2 — getAsync + setAsync (Mailbox 1.3+)
 *   - Fetches existing body, appends signature, writes back
 *   - Safe only for NEW compose (body is empty)
 *   - On reply/forward, appends after quoted text (acceptable fallback)
 *
 * Tier 3 — Silent failure
 *   - If both tiers fail, log the error and return without crashing
 *   - The user can retry via the task pane Insert button
 */
export function injectSignature(signatureHtml_1) {
    return __awaiter(this, arguments, void 0, function* (signatureHtml, options = {}) {
        var _a;
        const item = Office.context.mailbox.item;
        if (!item) {
            throw new Error("[signatureInjector] No active compose item");
        }
        if ((_a = options.inlineImages) === null || _a === void 0 ? void 0 : _a.length) {
            yield ensureInlineImages(item, options.inlineImages);
        }
        // Tier 1: setSignatureAsync (preferred)
        if (typeof item.body.setSignatureAsync === "function") {
            return new Promise((resolve, reject) => {
                item.body.setSignatureAsync(signatureHtml, { coercionType: Office.CoercionType.Html }, (result) => {
                    var _a;
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve();
                    }
                    else {
                        console.warn("[signatureInjector] setSignatureAsync failed, falling back:", (_a = result.error) === null || _a === void 0 ? void 0 : _a.message);
                        injectViaBodySet(item, signatureHtml).then(resolve).catch(reject);
                    }
                });
            });
        }
        // Tier 2: body.getAsync + body.setAsync
        return injectViaBodySet(item, signatureHtml);
    });
}
function injectViaBodySet(item, signatureHtml) {
    return __awaiter(this, void 0, void 0, function* () {
        const existingBody = yield getBodyAsync(item);
        // Append signature below any existing content with a clear separator div.
        // The id="ms-sig" lets future calls replace rather than double-append.
        const bodyWithSig = existingBody +
            `\n<div id="ms-sig" style="margin-top:20px;">${signatureHtml}</div>`;
        return setBodyAsync(item, bodyWithSig);
    });
}
function ensureInlineImages(item, inlineImages) {
    return __awaiter(this, void 0, void 0, function* () {
        const existingNames = new Set();
        if (!isMobilePlatform()) {
            const existingAttachments = yield getAttachmentsAsync(item);
            for (const attachment of existingAttachments) {
                if (attachment.isInline) {
                    existingNames.add(attachment.name.toLowerCase());
                }
            }
        }
        for (const image of inlineImages) {
            const normalizedName = image.fileName.toLowerCase();
            if (existingNames.has(normalizedName)) {
                continue;
            }
            yield addInlineAttachmentFromBase64(item, image.base64Content, image.fileName);
            existingNames.add(normalizedName);
        }
    });
}
function getBodyAsync(item) {
    return new Promise((resolve, reject) => {
        item.body.getAsync(Office.CoercionType.Html, (result) => {
            var _a, _b, _c;
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve((_a = result.value) !== null && _a !== void 0 ? _a : "");
            }
            else {
                reject(new Error((_c = (_b = result.error) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : "getAsync failed"));
            }
        });
    });
}
function getAttachmentsAsync(item) {
    return new Promise((resolve, reject) => {
        item.getAttachmentsAsync((result) => {
            var _a, _b, _c;
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve((_a = result.value) !== null && _a !== void 0 ? _a : []);
            }
            else {
                reject(new Error((_c = (_b = result.error) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : "getAttachmentsAsync failed"));
            }
        });
    });
}
function addInlineAttachmentFromBase64(item, base64Content, fileName) {
    return new Promise((resolve, reject) => {
        item.addFileAttachmentFromBase64Async(base64Content, fileName, { isInline: true }, (result) => {
            var _a, _b;
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve();
            }
            else {
                reject(new Error((_b = (_a = result.error) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : `addFileAttachmentFromBase64Async failed for ${fileName}`));
            }
        });
    });
}
function setBodyAsync(item, html) {
    return new Promise((resolve, reject) => {
        item.body.setAsync(html, { coercionType: Office.CoercionType.Html }, (result) => {
            var _a, _b;
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve();
            }
            else {
                reject(new Error((_b = (_a = result.error) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : "setAsync failed"));
            }
        });
    });
}
function isMobilePlatform() {
    var _a, _b;
    return (typeof Office !== "undefined" &&
        typeof Office.PlatformType !== "undefined" &&
        (((_a = Office.context) === null || _a === void 0 ? void 0 : _a.platform) === Office.PlatformType.iOS ||
            ((_b = Office.context) === null || _b === void 0 ? void 0 : _b.platform) === Office.PlatformType.Android));
}
